// src/components/AppLayout.js
import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import { auth, db } from "../services/firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import "./AppLayout.css";

function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(auth.currentUser);
  const [role, setRole] = useState("homeowner");
  const [menuOpen, setMenuOpen] = useState(false);

  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (!u) { setRole("guest"); return; }
      try {
        const snap = await getDoc(doc(db, "users", u.uid));
        const r = snap.exists() ? snap.data()?.role : null;
        setRole(String(r || "homeowner").toLowerCase());
      } catch {
        setRole("homeowner");
      }
    });
    return () => unsub();
  }, []);

  const isAdmin = useMemo(() => role === "admin", [role]);
  const isHomeowner = useMemo(() => role === "homeowner", [role]);
  const canSeeContractorDashboard = useMemo(
    () => role === "contractor" || role === "admin",
    [role]
  );

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/");
    } catch (e) {
      console.error("Logout failed:", e);
      alert("Logout failed. Check console.");
    }
  };

  return (
    <div className="app-layout">
      <header className="topbar">

        {/* Brand */}
        <Link to="/" className="brand-link">
          <img src="/logo.png" alt="MeasureSmart AI" className="brand-logo" />
        </Link>

        {/* Desktop nav — hidden on mobile */}
        <nav className="navlinks">
          <NavLink to="/measure">Home</NavLink>
          <NavLink to="/surface-mode">Surface Mode</NavLink>
	  <NavLink to="/kitchen-mode">Kitchen Mode</NavLink>
          <NavLink to="/multiroom">Multi-Room</NavLink>
          {isHomeowner && <NavLink to="/dashboard">Your Projects</NavLink>}
          {canSeeContractorDashboard && <NavLink to="/contractor">Contractor</NavLink>}
          {isAdmin && <NavLink to="/admin">Admin</NavLink>}
        </nav>

        {/* Desktop right — hidden on mobile */}
        <div className="topbar-right">
          <div className="account-meta">
            <div className="user-email">{user?.email || ""}</div>
            <div className="role-pill">{role}</div>
          </div>
          <button className="logout-btn" onClick={handleLogout}>Logout</button>
        </div>

        {/* Hamburger — mobile only */}
        <button
          className="hamburger"
          onClick={() => setMenuOpen((o) => !o)}
          aria-label="Toggle navigation menu"
        >
          <span className={`ham-line ${menuOpen ? "ham-line--open-1" : ""}`} />
          <span className={`ham-line ${menuOpen ? "ham-line--open-2" : ""}`} />
          <span className={`ham-line ${menuOpen ? "ham-line--open-3" : ""}`} />
        </button>
      </header>

      {/* Mobile drawer */}
      {menuOpen && (
        <div className="mobile-menu">
          <nav className="mobile-navlinks">
            <NavLink to="/measure">Home</NavLink>
            <NavLink to="/surface-mode">Surface Mode</NavLink>
            <NavLink to="/kitchen-mode">Kitchen Mode</NavLink>
            <NavLink to="/multiroom">Multi-Room Analyzer</NavLink>
            {isHomeowner && <NavLink to="/dashboard">Your Projects</NavLink>}
            {canSeeContractorDashboard && <NavLink to="/contractor">Contractor Dashboard</NavLink>}
            {isAdmin && <NavLink to="/admin">Admin Dashboard</NavLink>}
          </nav>
          <div className="mobile-menu-footer">
            <div className="mobile-user-email">{user?.email || ""}</div>
            <button className="mobile-logout-btn" onClick={handleLogout}>Logout</button>
          </div>
        </div>
      )}

      <main className="app-content">
        <Outlet />
      </main>

      <div
        style={{
          position: "fixed",
          bottom: 5,
          right: 10,
          fontSize: "10px",
          background: "rgba(0,0,0,0.6)",
          color: "white",
          padding: "3px 6px",
          borderRadius: "4px",
          zIndex: 9999,
        }}
      >
        {process.env.REACT_APP_BUILD_ENV} | {process.env.REACT_APP_BUILD_VERSION}
      </div>
    </div>
  );
}

export default AppLayout;
